import { Localized } from "@fluent/react"
import CloseIcon from "@mui/icons-material/Close"
import { DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { AppId } from "../../../app/appTypes"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { checkStructureNodes, uncheckAllStructureNodes } from "../../device/structureSlice"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { MultiSelectStructureComponent } from "../../structure/MultiSelectStructureComponent"
import { saveSupplierStructure, SupplierStructure } from "../suppliersSlice"

interface UpdateSupplierStructureDialogProps {
    supplierId: AppId
    selectedStructureIds: AppId[]
    onClose: () => void
    onSuccess: () => void
}

export const UpdateSupplierStructureDialog = ({
    supplierId,
    selectedStructureIds,
    onClose,
    onSuccess,
}: UpdateSupplierStructureDialogProps) => {
    const dispatch = useAppDispatch()
    const [selectedIds, setSelectedIds] = useState<AppId[]>(selectedStructureIds)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setSelectedIds(selectedStructureIds)
    }, [selectedStructureIds])

    const handleSave = useCallback(async () => {
        setLoading(true)
        try {
            const items: SupplierStructure[] = selectedIds.map((structureId) => ({
                supplierId,
                structureId,
            }))
            await dispatch(saveSupplierStructure([supplierId, items])).unwrap()
            dispatch(uncheckAllStructureNodes())
            dispatch(checkStructureNodes(selectedIds))
            dispatch(showSuccess("saved"))
            onSuccess()
        } catch {
            dispatch(showError("error"))
        } finally {
            setLoading(false)
        }
    }, [dispatch, onSuccess, selectedIds, supplierId])

    return (
        <>
            <DialogTitle sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Localized id="structure">_Struktura</Localized>
                <IconButton
                    onClick={onClose}
                    disabled={loading}
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 3, px: 3 }}>
                <MultiSelectStructureComponent
                    expandedOnOpen={true}
                    selectedIds={selectedIds}
                    onSelectionChange={setSelectedIds}
                    showSelectedChips={false}
                />
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <SaveButton loading={loading} onClick={handleSave} />
                <CancelButton loading={loading} onClick={onClose} />
            </DialogActions>
        </>
    )
}
